import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { PaymentMethod } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  paymentMethod: PaymentMethod | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function DeletePaymentModal({ open, onClose, paymentMethod, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!paymentMethod) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/payment-method/delete", {
        method: "POST",
        body: { paymentMethodId: paymentMethod.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to delete payment method");
      onSuccess("Payment method deleted");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to delete payment method");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.red }}>Delete Payment Method</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning">
          Are you sure you want to delete <strong>{paymentMethod?.payment_details?.brand} **** {paymentMethod?.payment_details?.last4}</strong>?
          This action cannot be undone.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Back</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}
          sx={{ bgcolor: COLORS.red, "&:hover": { bgcolor: "#B43A30" }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
